// ADR 25.9.26f §4 — the one door from the boot graph to the wasm side.
//
// Since 25.9.26f React renders before the wasm has arrived, and `runInit`
// **resolves** on failure (`wasm/report.ts`), so a page module that waits on
// the facade evaluates after a failed init as readily as after a good one —
// and then calls into an uninitialised wasm. This wrapper sits on every lazy
// edge into the wasm side (`router.tsx`'s `page()` and `ShellBody`'s two
// `React.lazy` factories) and keeps that from rendering.
//
// It starts the facade import and the page import **together**, so a signed-in
// deep link pays no serial chunk hop, and settles both with `allSettled`, so
// the page's promise has a handler from the moment it exists and a rejection
// can never reach Sentry's global handler unreported-by-design. Then:
//
//   • the facade import rejected, or the init outcome (`wasm/initOutcome.ts`)
//     is a recorded failure → a component that renders nothing, and the page's
//     outcome is discarded. `main.tsx` owns the one report and the refusal
//     that replaces the page;
//   • otherwise the page, or its rejection rethrown to `RouteFailure`.
//
// ADR 16.10.26p §3 — the outcome, never a slot on the facade's namespace,
// which Safari can hand back before the facade's body has run. It is awaited
// only after the facade resolved: a rejected import never settles it.
//
// This module is on the boot graph, so it never imports `../wasm` statically:
// `bootGraph.test.ts` (25.9.26f AC1) fails if it does.

import type { ComponentType } from 'react'

import { wasmInitOutcome } from '../wasm/initOutcome'

/** Renders nothing: the page past a failed init, which `main.tsx` is replacing. */
function Nothing(): null {
  return null
}

/**
 * The decision, over two promises already in flight. Split from `gated` so a
 * suite can drive it with stubs rather than with the real top-level `await`.
 */
export async function settleGate<P extends object>(
  facade: Promise<unknown>,
  page: Promise<ComponentType<P>>,
  outcome: Promise<unknown> = wasmInitOutcome,
): Promise<ComponentType<P>> {
  const [imported, loaded] = await Promise.allSettled([facade, page])
  if (imported.status === 'rejected' || (await outcome) !== null) {
    return Nothing as ComponentType<P>
  }
  if (loaded.status === 'rejected') throw loaded.reason
  return loaded.value
}

/** One named export of a lazily-loaded module, behind the gate. */
export function gated<P extends object, K extends string>(
  load: () => Promise<Record<K, ComponentType<P>>>,
  name: K,
): Promise<ComponentType<P>> {
  return settleGate(
    import('../wasm'),
    load().then((module) => module[name]),
  )
}
